'use client';

import { useState, useEffect } from 'react';
import { api } from '../../core/apiClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Edit2, Trash2, Plus } from 'lucide-react';

interface Journal {
  id: number;
  code: string;
  name: string;
  type: string;
  status: string;
  default_account?: {
    id: number;
    code: string;
    name: string;
  };
}

const JOURNAL_TYPES = ['General', 'Vendes', 'Compres', 'Banc', 'Caixa'];

export default function Journals() {
  const [journals, setJournals] = useState<Journal[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: 'General',
    status: 'Actiu',
  });

  useEffect(() => {
    loadJournals();
  }, []);

  const loadJournals = async () => {
    try {
      const response: any = await api.get('/comptabilitat/journals');
      if (response.success) {
        setJournals(response.data || []);
      }
    } catch (error) {
      console.error('Error carregant diaris:', error);
    }
  };

  const handleAddJournal = async () => {
    if (!formData.code || !formData.name) {
      alert('Cal omplir tots els camps obligatoris');
      return;
    }

    try {
      if (editingId) {
        await api.put(`/comptabilitat/journals/${editingId}`, formData);
      } else {
        await api.post('/comptabilitat/journals', formData);
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ code: '', name: '', type: 'General', status: 'Actiu' });
      loadJournals();
      alert('Diari desat correctament');
    } catch (error: any) {
      alert('Error: ' + (error.message || 'Error desconegut'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Estàs segur que vols eliminar aquest diari?')) {
      return;
    }

    try {
      await api.delete(`/comptabilitat/journals/${id}`);
      loadJournals();
      alert('Diari eliminat correctament');
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
  };

  const handleEdit = (journal: Journal) => {
    setEditingId(journal.id);
    setFormData({
      code: journal.code,
      name: journal.name,
      type: journal.type,
      status: journal.status,
    });
    setShowForm(true);
  };

  return (
    <div className="flex flex-col gap-6 p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Diaris Comptables</h1>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Nou Diari
        </Button>
      </div>

      {showForm && (
        <Card className="p-6">
          <h2 className="text-sm font-bold mb-4">
            {editingId ? 'Editar Diari' : 'Crear Diari'}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Codi</label>
              <Input
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
                placeholder="GEN"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nom</label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Diari General"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Tipus</label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                {JOURNAL_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Estat</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option>Actiu</option>
                <option>Inactiu</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <Button
              onClick={handleAddJournal}
              className="bg-green-600 hover:bg-green-700"
            >
              Desar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setFormData({
                  code: '',
                  name: '',
                  type: 'General',
                  status: 'Actiu',
                });
              }}
            >
              Cancel·lar
            </Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="p-4 border-b grid grid-cols-5 gap-4 text-[12px] font-medium text-muted-foreground">
          <div>Codi</div>
          <div>Nom</div>
          <div>Tipus</div>
          <div>Estat</div>
          <div className="text-right">Accions</div>
        </div>
        {journals.length > 0 ? (
          journals.map((journal) => (
            <div
              key={journal.id}
              className="p-4 border-b grid grid-cols-5 gap-4 hover:bg-gray-50"
            >
              <div className="font-mono">{journal.code}</div>
              <div>{journal.name}</div>
              <div className="text-sm text-gray-600">{journal.type}</div>
              <div>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    journal.status === 'Actiu' || journal.status === 'Activo'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {journal.status === 'Activo' ? 'Actiu' : journal.status === 'Inactivo' ? 'Inactiu' : journal.status}
                </span>
              </div>
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(journal)}
                  title="Editar"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(journal.id)}
                  title="Eliminar"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="p-6 text-center text-gray-500">
            No hi ha diaris registrats
          </div>
        )}
      </Card>
    </div>
  );
}
